import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { weatherConditionToIcon } from 'src/Containers/Community/CommunityHome/CommunityTheFuture/WeatherForMyAssets/WeatherDisplay/weatherConditionToIcon/weatherConditionToIcon';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import useFetchWeatherForMyAssets from 'src/Hooks/Queries/useFetchWeatherForMyAssets';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { DashboardEventDto } from 'src/Services/API/generated';

import styles from './DashboardHero.module.scss';

interface Props {
  nextStay?: DashboardEventDto;
}

const DashboardHero = ({ nextStay }: Props) => {
  const { t } = useTranslation();
  const assetId = useCurrentAssetId();
  const { data: assetDetails } = useFetchAssetDetails();
  const { data: weatherData, refetch: refetchWeather } = useFetchWeatherForMyAssets();

  // Force refetch weather on dashboard mount (the hook caches for 60 min)
  useEffect(() => {
    refetchWeather();
  }, [refetchWeather]);

  const currentWeather = useMemo(() => {
    if (!weatherData || !assetId) return null;
    return weatherData.find(w => w.assets.some(a => a.id === assetId)) ?? null;
  }, [weatherData, assetId]);

  const imageUrl = assetDetails?.imageUrl;
  const hasWeather = currentWeather?.currentConditions != null;

  const nextStayCaption = useMemo(() => {
    if (!nextStay) return null;
    const daysUntil = dayjs(nextStay.startTime).startOf('day').diff(dayjs().startOf('day'), 'day');
    // Stays that started already or start today do not read as "in N days".
    if (daysUntil < 0) return null;
    return t('dashboard.hero.nextStay', {
      defaultValue: 'Prochain séjour : {{label}}, dans {{count}} jour',
      defaultValue_other: 'Prochain séjour : {{label}}, dans {{count}} jours',
      count: daysUntil,
      label: nextStay.name,
    });
  }, [nextStay, t]);

  if (!imageUrl && !hasWeather) return null;

  const temp = currentWeather?.currentConditions
    ? Math.round(currentWeather.currentConditions.temperature.degrees)
    : null;
  const weatherLabel = currentWeather?.currentConditions?.weatherCondition.description.text;
  const WeatherIcon = currentWeather?.currentConditions
    ? weatherConditionToIcon(currentWeather.currentConditions.weatherCondition)
    : null;

  return (
    <div
      className={styles.container}
      style={imageUrl ? { backgroundImage: `url(${imageUrl})` } : undefined}
    >
      {imageUrl && <div className={styles.overlay} />}

      <div className={styles.content}>
        {hasWeather && WeatherIcon && (
          <div className={styles.weather}>
            <WeatherIcon size={28} className={styles.weatherIcon} />
            <span className={styles.temperature}>{temp}°</span>
            <span className={styles.weatherLabel}>{weatherLabel}</span>
          </div>
        )}

        <h2 className={styles.assetName}>{assetDetails?.name}</h2>
        {nextStayCaption && <p className={styles.nextStay}>{nextStayCaption}</p>}
      </div>
    </div>
  );
};

export default DashboardHero;
